const CHAT_FOLLOWUP_CHANNEL_KEYS = ['personal', 'event', 'group'];

const toSafeBadgeCount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return Math.trunc(numeric);
};

const getSummarySource = (summary = {}) =>
  summary?.summary && typeof summary.summary === 'object' ? summary.summary : summary || {};

const pickCount = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value == null) continue;
    return toSafeBadgeCount(value);
  }
  return 0;
};

export const normalizeChatFollowupMineCountsByChannel = (summary = {}) => {
  const source = getSummarySource(summary);
  const nested =
    source?.mineCountsByChannel ||
    source?.mine_counts_by_channel ||
    source?.assignedToMeCountsByChannel ||
    source?.assigned_to_me_counts_by_channel ||
    source?.myOpenCountsByChannel ||
    source?.my_open_counts_by_channel ||
    {};

  return {
    personal: pickCount(nested, ['personal', 'artist', 'artistChat', 'artist_chat', 'personalChat', 'personal_chat']) ||
      pickCount(source, ['minePersonalCount', 'mine_personal_count', 'personalMineCount', 'personal_mine_count']),
    event: pickCount(nested, ['event', 'eventChat', 'event_chat']) ||
      pickCount(source, ['mineEventCount', 'mine_event_count', 'eventMineCount', 'event_mine_count']),
    group: pickCount(nested, ['group', 'groupChat', 'group_chat']) ||
      pickCount(source, ['mineGroupCount', 'mine_group_count', 'groupMineCount', 'group_mine_count']),
  };
};

export const extractChatFollowupMineCount = (summary = {}) => {
  const source = getSummarySource(summary);
  const explicit = pickCount(source, [
    'mineCount',
    'mine_count',
    'assignedToMeCount',
    'assigned_to_me_count',
    'myOpenCount',
    'my_open_count',
  ]);
  if (explicit > 0) return explicit;
  const counts = normalizeChatFollowupMineCountsByChannel(source);
  return CHAT_FOLLOWUP_CHANNEL_KEYS.reduce((total, key) => total + toSafeBadgeCount(counts[key]), 0);
};

export const formatMenuBadgeCount = (value) => {
  const count = toSafeBadgeCount(value);
  return count > 99 ? '99+' : String(count);
};

const TONE_CLASS_NAME = {
  mention: 'sidebar-count-badge--mention',
  ok: 'sidebar-count-badge--ok',
  alert: 'sidebar-count-badge--alert',
};

export const buildAdminChatMenuBadge = ({
  unreadCount = 0,
  unreadTone = null,
  aiMineCount = 0,
  hasMention = false,
} = {}) => {
  const normalizedUnreadCount = toSafeBadgeCount(unreadCount);
  const normalizedAiMineCount = toSafeBadgeCount(aiMineCount);
  const count = normalizedUnreadCount + normalizedAiMineCount;
  if (count <= 0) {
    return {
      count: 0,
      hasBadge: false,
      tone: null,
      toneClassName: '',
      unreadCount: normalizedUnreadCount,
      aiMineCount: normalizedAiMineCount,
    };
  }

  const normalizedUnreadTone = String(unreadTone || '').toLowerCase();
  // 未読にメンション（自分宛）が含まれる時は最優先で 'mention'（黄色/amber）。
  const tone = hasMention
    ? 'mention'
    : normalizedAiMineCount > 0
      ? 'alert'
      : normalizedUnreadTone === 'ok'
        ? 'ok'
        : 'alert';

  return {
    count,
    hasBadge: true,
    tone,
    toneClassName: TONE_CLASS_NAME[tone] || TONE_CLASS_NAME.alert,
    unreadCount: normalizedUnreadCount,
    aiMineCount: normalizedAiMineCount,
  };
};

export const buildAdminChatMenuBadges = ({
  artistUnreadCount = 0,
  artistUnreadTone = null,
  eventUnreadCount = 0,
  eventUnreadTone = null,
  eventHasMention = false,
  groupUnreadCount = 0,
  groupUnreadTone = null,
  groupHasMention = false,
  followupSummary = {},
} = {}) => {
  const mineCountsByChannel = normalizeChatFollowupMineCountsByChannel(followupSummary);
  return {
    // 個別(1:1)チャットにメンションは無いため hasMention は付与しない。
    artist: buildAdminChatMenuBadge({
      unreadCount: artistUnreadCount,
      unreadTone: artistUnreadTone,
      aiMineCount: mineCountsByChannel.personal,
    }),
    event: buildAdminChatMenuBadge({
      unreadCount: eventUnreadCount,
      unreadTone: eventUnreadTone,
      aiMineCount: mineCountsByChannel.event,
      hasMention: eventHasMention,
    }),
    group: buildAdminChatMenuBadge({
      unreadCount: groupUnreadCount,
      unreadTone: groupUnreadTone,
      aiMineCount: mineCountsByChannel.group,
      hasMention: groupHasMention,
    }),
    mineCount: extractChatFollowupMineCount(followupSummary),
    mineCountsByChannel,
  };
};
